'use client';
// Modal xác nhận dùng chung toàn app — thay thế window.confirm() thô.
// Dùng: `import { confirmDialog } from '@/components/ConfirmDialog'` rồi
//   `if (!(await confirmDialog('Xoá mục này?'))) return;`
// hoặc giàu hơn: `await confirmDialog({ message, title, confirmLabel, tone: 'rose' })`.
// Singleton mức module: ConfirmProvider (mount 1 lần ở root Providers) đăng ký handler;
// confirmDialog() gọi handler → trả Promise<boolean>. Trước khi provider mount (hiếm) →
// fallback window.confirm để không bao giờ "nuốt" xác nhận.
import React, { useCallback, useEffect, useState } from 'react';

type Tone = 'brand' | 'rose' | 'amber';
export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
}
type Req = ConfirmOptions & { resolve: (v: boolean) => void };

const toOpts = (o: ConfirmOptions | string): ConfirmOptions => (typeof o === 'string' ? { message: o } : o);

let externalConfirm: ((o: ConfirmOptions | string) => Promise<boolean>) | null = null;

/** Mở modal xác nhận. Trả Promise<true> nếu người dùng bấm nút xác nhận, false nếu huỷ/đóng. */
export function confirmDialog(o: ConfirmOptions | string): Promise<boolean> {
  if (externalConfirm) return externalConfirm(o);
  if (typeof window !== 'undefined') return Promise.resolve(window.confirm(toOpts(o).message));
  return Promise.resolve(false);
}

/** Hook tiện dụng (tương đương import confirmDialog trực tiếp). */
export function useConfirm() { return confirmDialog; }

const TONE_BTN: Record<Tone, string> = {
  brand: 'bg-brand-600 hover:bg-brand-700',
  rose: 'bg-rose-600 hover:bg-rose-700',
  amber: 'bg-amber-600 hover:bg-amber-700',
};

export function ConfirmProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [req, setReq] = useState<Req | null>(null);

  const run = useCallback((o: ConfirmOptions | string) => new Promise<boolean>(resolve => {
    setReq({ ...toOpts(o), resolve });
  }), []);

  useEffect(() => { externalConfirm = run; return () => { externalConfirm = null; }; }, [run]);

  const close = useCallback((v: boolean) => { setReq(cur => { cur?.resolve(v); return null; }); }, []);

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req, close]);

  const tone = req?.tone ?? 'brand';

  return (
    <>
      {children}
      {req && (
        <div className="fixed inset-0 bg-black/40 z-[300] flex items-end sm:items-center justify-center p-0 sm:p-4"
          role="button" tabIndex={-1} onClick={() => close(false)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') close(false); }}>
          <div className="bg-[var(--card)] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm p-5" role="dialog" aria-modal="true"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}>
            {req.title && <h4 className="font-semibold text-[var(--foreground)] mb-1">{req.title}</h4>}
            <p className="text-sm text-[var(--foreground)] mb-4 leading-relaxed whitespace-pre-line">{req.message}</p>
            <div className="flex gap-2">
              <button autoFocus onClick={() => close(true)}
                className={`flex-1 py-2 text-white text-sm font-medium rounded-xl transition-colors ${TONE_BTN[tone]}`}>
                {req.confirmLabel ?? 'Xác nhận'}
              </button>
              <button onClick={() => close(false)}
                className="flex-1 py-2 border border-[var(--border)] text-sm rounded-xl hover:bg-[var(--background)] transition-colors">
                {req.cancelLabel ?? 'Đóng'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
